// src/pages/Planning.jsx
import * as React from 'react';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocalization } from '@progress/kendo-react-intl';
import { Card, CardHeader, Avatar, CardTitle, CardSubtitle } from '@progress/kendo-react-layout';
import { guid } from '@progress/kendo-react-common';
import { Scheduler } from './../components/Scheduler';
import config from '../config/config';
import { FormWithCustomDialog } from './../components/form/CustomForm';
import './../styles/Planning.css';

/* ---------- Hilfsfunktionen --------------------------------------- */
const toLocal = (utc) => new Date(utc.getTime() - utc.getTimezoneOffset() * 60000);

// Hilfsfunktion, um ungültige oder Epoch‑Datumswerte herauszufiltern
const isInvalidDate = (d) => !d || isNaN(d) || d.getFullYear() === 1970;

const formatJobs = (rawJobs, employees, customers, updatedItems = []) => {
  const empMap = new Map(employees.map((e) => [e.id, e]));

  return rawJobs.flatMap((job) => {
    // 1) Aktualisierte Elemente heranziehen (Drag‑&‑Drop o. ä.)
    const upd = updatedItems.find((u) => u?.id === job.JobID);

    // 2) Start‑/Ende‑UTC bestimmen – ohne Fallback auf heutiges Datum
    const startUTC = upd?.start ?? (job.AusfuehrungszeitStart ? new Date(job.AusfuehrungszeitStart) : null);
    const endUTC   = upd?.end   ?? (job.AusfuehrungszeitEnde  ? new Date(job.AusfuehrungszeitEnde)  : null);

    // 3) Ereignisse ohne gültiges Start‑ oder Enddatum komplett ausfiltern
    if (isInvalidDate(startUTC) || isInvalidDate(endUTC)) {
      return []; // ⟵ nichts zurückgeben ⇒ Eintrag wird verworfen
    }

    // 4) Zuordnungen zu Mitarbeitenden ermitteln
    const employeesForJob = Array.isArray(job.AssignedEmployee)
      ? job.AssignedEmployee.map((id) => empMap.get(id)).filter(Boolean)
      : [empMap.get(job.AssignedEmployee)].filter(Boolean);

    const base = {
      description: `${customers[job.CustomerID] || 'Unknown Company'}\n${job.JobIDcustom || ''}`,
      start: toLocal(startUTC),
      end: toLocal(endUTC),
      originalJobID: job.JobID,
      isAllDay: job.isAllDay || upd?.isAllDay || false,
    };

    // 5) Eintrag ohne Mitarbeiter → Platzhalter erzeugen
    if (employeesForJob.length === 0) {
      return [{
        ...base,
        id: job.JobID,
        title: `Unassigned - ${job.JobIDcustom || ''}`,
        employeeID: null,
        employeeColor: '#A52A2A',
      }];
    }

    // 6) Für jeden zugewiesenen Mitarbeiter einen Scheduler‑Termin erzeugen
    return employeesForJob.map((emp) => ({
      ...base,
      id: `${job.JobID}-${emp.id}`,
      title: `${emp.fullName} - ${job.JobIDcustom || ''}`,
      employeeID: emp.id,
      employeeColor: emp.userColorCode,
    }));
  });
};

/* ---------- Hauptkomponente --------------------------------------- */
const Planning = () => {
  const localizationService = useLocalization();

  const [employees, setEmployees]     = useState([]);
  const [customers, setCustomers]     = useState({});
  const [data, setData]               = useState([]);
  const [filterState, setFilterState] = useState({});
  const [currentView, setCurrentView] = useState('week');

  const JOB_CACHE_KEY = 'exploserv_jobs_cache_v1';
  const JOB_ETAG_KEY  = 'exploserv_jobs_etag_v1';
  const validViews    = ['day', 'week', 'work-week', 'month'];
  const employeesLoaded = employees.length > 0;

  /* ---------- 1) Mitarbeiter laden -------------------------------- */
  useEffect(() => {
    fetch(`${config.host}/users`)
      .then((res) => res.json())
      .then((users) => {
        const formatted = users.map((u) => ({
          id: u.BenutzerID,
          fullName: `${u.Vorname} ${u.Nachname}`,
          avatar: `${config.host}/get_image/${u.AvatarThumbLink}`,
          employeeRole: u.EmployeeRole,
          userColorCode: u.UserColorCode || '#A52A2A',
        }));
        setEmployees(formatted);
        setFilterState(formatted.reduce((acc, e) => ({ ...acc, [e.id]: true }), {}));
      });
  }, []);

  /* ---------- 2) Cache sofort anzeigen ---------------------------- */
  useEffect(() => {
    if (!employeesLoaded) return;
    try {
      const raw = JSON.parse(localStorage.getItem(JOB_CACHE_KEY) || '[]');
      if (raw.length) setData(formatJobs(raw, employees, {}));
    } catch { /* ignore */ }
  }, [employeesLoaded, employees]);

  /* ---------- 3) Jobs vom Server (ETag) --------------------------- */
  const fetchAndUpdateJobs = useCallback(async (updatedItems = []) => {
    try {
      const etag = localStorage.getItem(JOB_ETAG_KEY);
      const headers = etag ? { 'If-None-Match': etag } : {};
      const resp = await fetch(`${config.host}/jobs`, { headers });

      const rawJobs = resp.status === 304
        ? JSON.parse(localStorage.getItem(JOB_CACHE_KEY) || '[]')
        : await resp.json();

      if (resp.status !== 304) {
        localStorage.setItem(JOB_CACHE_KEY, JSON.stringify(rawJobs));
        const newEtag = resp.headers.get('ETag');
        if (newEtag) localStorage.setItem(JOB_ETAG_KEY, newEtag);
      }

      const validJobs   = rawJobs.filter((j) => j && j.JobID);
      const customerIds = [...new Set(validJobs.map((j) => j.CustomerID).filter(Boolean))];
      const customerMap = {};

      if (customerIds.length) {
        const resps = await Promise.all(
          customerIds.map((id) => fetch(`${config.host}/customers/${id}`).then((r) => r.json()))
        );
        resps.forEach((c) => {
          const cid = c['Customer-ID'] || c.CustomerID || c.id;
          if (cid) customerMap[cid] = c.Firma;
        });
        setCustomers(customerMap);
      }

      setData(formatJobs(validJobs, employees, customerMap, updatedItems));
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  }, [employees]);

  useEffect(() => { if (employeesLoaded) fetchAndUpdateJobs(); }, [employeesLoaded, fetchAndUpdateJobs]);

  /* ---------- 4) Scheduler-Ressourcen stabil halten --------------- */
  const employeeResources = useMemo(() => [
    {
      name: 'Employees',
      data: employees.map((e) => ({
        employeeID:   e.id,
        employeeName: e.fullName,
        color:        e.userColorCode,
      })),
      field:      'employeeID',
      valueField: 'employeeID',
      textField:  'employeeName',
      colorField: 'color',
    },
  ], [employees]);

  /* ---------- 5) Scheduler-Callbacks ------------------------------ */
  const onDataChange = useCallback(async ({ created = [], updated = [], deleted = [] }) => {
    if (updated.length) {
      const valid = updated.filter((i) => i && i.originalJobID);
      await Promise.all(valid.map(async (item) => {
        const startUTC = new Date(item.start.getTime() + item.start.getTimezoneOffset() * 60000);
        const endUTC   = new Date(item.end.getTime() + item.end.getTimezoneOffset() * 60000);
        await fetch(`${config.host}/jobs/${item.originalJobID}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            AusfuehrungszeitStart: startUTC.toISOString(),
            AusfuehrungszeitEnde:  endUTC.toISOString(),
            isAllDay: item.isAllDay,
          }),
        });
      }));
      await fetchAndUpdateJobs(updated);
    }

    if (created.length || deleted.length) {
      localStorage.removeItem(JOB_CACHE_KEY);
      localStorage.removeItem(JOB_ETAG_KEY);
      await fetchAndUpdateJobs();
    }
  }, [fetchAndUpdateJobs]);

  /* ---------- 6) UI-Hilfen --------------------------------------- */
  const onEmployeeClick = (id) => setFilterState((p) => ({ ...p, [id]: !p[id] }));
  const handleViewChange = (e) => validViews.includes(e.value) && setCurrentView(e.value);

  /* ---------- 7) Render ------------------------------------------ */
  return (
    <div id="Planning" className="planning-page main-content">
      <div className="card-container grid">
        <h3 className="card-title">
          Exploserv {localizationService.toLanguageString('custom.teamCalendar')}
        </h3>

        {employees.map((emp) => (
          <div
            key={emp.id}
            onClick={() => onEmployeeClick(emp.id)}
            style={!filterState[emp.id] ? { opacity: 0.5 } : {}}
          >
            <Card style={{ borderWidth: 0, cursor: 'pointer' }}>
              <CardHeader className="k-hbox">
                <Avatar type="image" shape="circle" size="large" style={{ borderWidth: 2 }}>
                  <div
                    className="k-avatar-image"
                    style={{
                      backgroundImage: `url(${emp.avatar})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center center',
                    }}
                  />
                </Avatar>
                <div>
                  <CardTitle>{emp.fullName}</CardTitle>
                  <CardSubtitle>{emp.employeeRole}</CardSubtitle>
                </div>
              </CardHeader>
            </Card>
          </div>
        ))}

        {employeesLoaded && (
          <div className="card-component" style={{ height: '100vh' }}>
            <Scheduler
              data={data.filter((e) => e && filterState[e.employeeID] !== false)}
              onDataChange={onDataChange}
              defaultView={currentView}
              view={currentView}
              onViewChange={handleViewChange}
              modelFields={{
                id: 'id',
                title: 'title',
                description: 'description',
                start: 'start',
                end: 'end',
                employeeID: 'employeeID',
                originalJobID: 'originalJobID',
                isAllDay: 'isAllDay',
              }}
              resources={employeeResources}
              form={FormWithCustomDialog}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Planning;
